import type { RefCallback } from 'react';
import { useCallback, useRef } from 'react';

class RefMap<K, V> {
  map: Map<K, V> = new Map();

  refCallbacks: Map<K, RefCallback<V>> = new Map();

  ref(key: K): RefCallback<V> {
    const refCallback = this.refCallbacks.get(key);

    if (refCallback) return refCallback;

    const callback: RefCallback<V> = value => {
      if (value) {
        this.refCallbacks.set(key, callback);
        this.map.set(key, value);
      } else {
        this.refCallbacks.delete(key);
        this.map.delete(key);
      }
    };

    // Don't cache the callback until it's invoked as a ref, because if the
    // render is aborted and the element is never rendered, the ref will never
    // be invoked with `null` and therefore the cached callback will never be
    // removed from the map.
    return callback;
  }
}

/**
 * A hook for managing a map of `ref`s. This is useful for cases where a list
 * component needs refs to its child list items.
 *
 * @see {@link https://github.com/Macil/react-multi-ref}
 *
 * @returns a `getRef` function for getting the ref to pass to a component for a
 * given key, and a `getElement` function to get the element currently assigned
 * to the ref for a given key
 */
export const useRefMap = <K, V = HTMLElement>() => {
  const { current: refMap } = useRef(new RefMap<K, V>());

  return {
    getRef: useCallback((key: K) => refMap.ref(key), [refMap]),
    getElement: useCallback((key: K) => refMap.map.get(key), [refMap]),
    getElements: useCallback(() => Array.from(refMap.map.values()), [refMap]),
  };
};
